import { CreateEmployeeDto, Employee, EmployeeRole } from "@/types/v2";
import { useStore } from "@/stores/useStore";
import { DataTable } from "@/components/Base/Datatable";
import {
  DatatableEmployee,
  employeeColumns,
} from "@/components/Employees/Employees.columns";
import { employee as employeeActions } from "@/repositories/employee";
import { user as userActions } from "@/repositories/user";
import { useEffect, useMemo, useRef, useState } from "react";
import { Skeleton } from "@/components/Base/Skeleton";
import { captureException, sentry } from "@/services/sentry";
import { employeeRowActions } from "@/components/Employees/Employees.actions";
import { DeleteEmployeeDialog } from "@/components/Employees/DeleteEmployee.dialog";
import { MergeEmployeeDialog } from "@/components/Employees/MergeEmployee.dialog";
import { RestoreEmployeeDialog } from "@/components/Employees/RestoreEmployee.dialog";
import { ReactivateEmployeeDialog } from "@/components/Employees/ReactivateEmployee.dialog";
import {
  activateEmployeeAccount,
  RoleNotSavedError,
} from "@/components/Employees/activateEmployeeAccount";
import { useRoles } from "@/hooks/useRoles";
import {
  EMPLOYEES_WITH_AUTH_QUERY_KEY,
  patchEmployeeInAllCachedLists,
  useInvalidateEmployeesWithAuth,
} from "@/components/Employees/useInvalidateEmployeesWithAuth";
import { EmployeeDialog } from "@/components/Employees/Employee.dialog";
import { toast } from "@/hooks/useToast";
import { useQuery, useQueryClient } from "react-query";
import { useSearchParams } from "react-router-dom";
import { PaginationState, SortingState } from "@tanstack/react-table";
import { compareValues } from "@/lib/compareValues";
import { useTerminologyReplacer } from "@/hooks/useTerminologyReplacer";
import {
  findLiveEmployeeWithEmail,
  getServiceAdvisorColor,
  getTableEmployees,
  withLiveEmployeeData,
} from "@/lib/employees";

const useEmployeesWithAuthStatus = (
  employees: Employee[] | undefined,
  shopId: string | undefined
) => {
  return useQuery({
    queryKey: [
      EMPLOYEES_WITH_AUTH_QUERY_KEY,
      shopId,
      employees?.map((e) => e.id).join(","),
    ],
    queryFn: async (): Promise<DatatableEmployee[]> => {
      if (!employees) return [];

      return Promise.all(
        employees.map(async (e): Promise<DatatableEmployee> => {
          const authUser = await userActions
            .getId(e.email)
            .catch(() => undefined);
          return {
            ...e,
            authAccountStatus: !authUser
              ? "doesNotExist"
              : authUser.data.disabled
                ? "inactive"
                : "active",
            hasLoggedIn: !!authUser?.data.metadata?.lastSignInTime,
          };
        })
      );
    },
    enabled: !!employees && !!shopId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
  });
};

interface EmployeesTableProps {
  /** Super admins only: also list soft-deleted employees (with a Restore action). */
  includeDeleted?: boolean;
}

export const EmployeesTable = ({
  includeDeleted = false,
}: EmployeesTableProps) => {
  const liveEmployees = useStore((state) => state.employees);
  const employeesIncludingDeleted = useStore(
    (state) => state.employeesIncludingDeleted
  );
  const { isSuperAdmin } = useRoles();
  const employees = getTableEmployees({
    employees: liveEmployees,
    employeesIncludingDeleted,
    includeDeleted,
    isSuperAdmin,
  });
  const shopId = useStore((state) => state.shopId);
  const { terminolizeAuto } = useTerminologyReplacer();
  const queryClient = useQueryClient();
  const invalidateEmployeesWithAuth = useInvalidateEmployeesWithAuth(shopId);
  const [searchParams, setSearchParams] = useSearchParams();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dialogAction, setDialogAction] = useState<
    "update" | "delete" | "restore" | "reactivate" | "merge"
  >("delete");
  const [activeEmployee, setActiveEmployee] = useState<Employee>();
  const [activeEmployeeAuthStatus, setActiveEmployeeAuthStatus] = useState<
    "active" | "inactive" | "doesNotExist"
  >("doesNotExist");
  const [employeeDialogOpen, setEmployeeDialogOpen] = useState(false);

  const { data: cachedRows, isLoading } = useEmployeesWithAuthStatus(
    employees,
    shopId
  );
  // The cached rows can be minutes behind the store (see withLiveEmployeeData).
  const data = useMemo(
    () =>
      cachedRows ? withLiveEmployeeData(cachedRows, employees) : undefined,
    [cachedRows, employees]
  );

  // Get values from URL params
  const currentPage = parseInt(searchParams.get("page") || "1", 10);
  const pageSize = parseInt(searchParams.get("pageSize") || "25", 10);
  const sortBy = searchParams.get("sortBy");
  const sortOrder = searchParams.get("sortOrder") as "asc" | "desc";

  // Helper function to get sorting from params
  const getSortingFromParams = (): SortingState => {
    if (sortBy && sortOrder) {
      return [{ id: sortBy, desc: sortOrder === "desc" }];
    }
    return [];
  };

  // Initialize state from URL params
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: currentPage - 1,
    pageSize: pageSize,
  });

  const [sorting, setSorting] = useState<SortingState>(getSortingFromParams());

  // Update pagination state when URL changes (browser back/forward)
  useEffect(() => {
    const newPageIndex = currentPage - 1;
    const newPageSize = pageSize;

    // Only update if values actually changed
    if (
      pagination.pageIndex !== newPageIndex ||
      pagination.pageSize !== newPageSize
    ) {
      setPagination({
        pageIndex: newPageIndex,
        pageSize: newPageSize,
      });
    }
  }, [currentPage, pageSize]); // Remove pagination from dependencies

  // Showing/hiding deleted employees changes the row count, so go back to
  // page 1 rather than risk landing past the end. Skips the first render so a
  // bookmarked ?page=N survives a reload.
  const previousIncludeDeleted = useRef(includeDeleted);
  useEffect(() => {
    if (previousIncludeDeleted.current === includeDeleted) return;
    previousIncludeDeleted.current = includeDeleted;
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, [includeDeleted]);

  // Update sorting state when URL changes
  useEffect(() => {
    const newSorting = getSortingFromParams();

    // Only update if sorting actually changed
    const sortingChanged =
      sorting.length !== newSorting.length ||
      (newSorting.length > 0 &&
        (sorting.length === 0 ||
          sorting[0].id !== newSorting[0].id ||
          sorting[0].desc !== newSorting[0].desc));

    if (sortingChanged) {
      setSorting(newSorting);
    }
  }, [sortBy, sortOrder]); // Use specific params instead of searchParams

  // Update URL when pagination changes (but only from user interaction)
  useEffect(() => {
    const urlPage = currentPage;
    const urlPageSize = parseInt(searchParams.get("pageSize") || "10", 10);
    const actualPage = pagination.pageIndex + 1;

    // Only update URL if pagination state differs from URL state
    if (actualPage !== urlPage || pagination.pageSize !== urlPageSize) {
      const newParams = new URLSearchParams(searchParams);
      newParams.set("page", actualPage.toString());
      newParams.set("pageSize", pagination.pageSize.toString());
      setSearchParams(newParams);
    }
  }, [pagination.pageIndex, pagination.pageSize]); // Remove searchParams from dependencies

  // Handle sorting changes and reset pagination
  const handleSortingChange = (
    updaterOrValue: SortingState | ((old: SortingState) => SortingState)
  ) => {
    const newSorting =
      typeof updaterOrValue === "function"
        ? updaterOrValue(sorting)
        : updaterOrValue;

    setSorting(newSorting);

    // Update URL with new sorting and reset to page 1
    const newParams = new URLSearchParams(searchParams);

    if (newSorting.length > 0) {
      const sort = newSorting[0];
      newParams.set("sortBy", sort.id);
      newParams.set("sortOrder", sort.desc ? "desc" : "asc");
    } else {
      newParams.delete("sortBy");
      newParams.delete("sortOrder");
    }

    // Reset to page 1 when sorting changes
    newParams.set("page", "1");
    setSearchParams(newParams);

    // Update local pagination state immediately to prevent flicker
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  const tableData = useMemo(() => {
    // Sort the data based on the current sorting state
    if (!data) return [];
    return [...data].sort((a, b) => {
      if (sorting.length === 0) return 0; // No sorting applied

      const sort = sorting[0];

      let defaultValueA: string | number | boolean = "";
      let defaultValueB: string | number | boolean = "";
      if (sort.id === "hideInShopHero") {
        defaultValueA = false; // Default value for boolean fields
        defaultValueB = false;
      } else if (sort.id === "color") {
        defaultValueA = getServiceAdvisorColor(a.id, [a]);
        defaultValueB = getServiceAdvisorColor(b.id, [b]);
      }

      const aValue = a[sort.id as keyof DatatableEmployee] ?? defaultValueA;
      const bValue = b[sort.id as keyof DatatableEmployee] ?? defaultValueB;

      return compareValues(aValue, bValue, sorting[0].desc);
    });
  }, [data, sorting]);

  const actions = (employee: DatatableEmployee) =>
    employeeRowActions({
      employee,
      onEdit: handleEdit,
      onDelete: handleDelete,
      onRestore: handleRestore,
      onMerge: isSuperAdmin ? handleMerge : undefined,
    });

  const handleDelete = (employeeData: DatatableEmployee) => {
    const { authAccountStatus, ...employee } = employeeData;
    setDialogAction("delete");
    setActiveEmployee(employee);
    setActiveEmployeeAuthStatus(authAccountStatus);
    setEmployeeDialogOpen(true);
  };

  const handleRestore = (employeeData: DatatableEmployee) => {
    const { authAccountStatus, ...employee } = employeeData;
    setDialogAction("restore");
    setActiveEmployee(employee);
    setActiveEmployeeAuthStatus(authAccountStatus);
    setEmployeeDialogOpen(true);
  };

  const handleMerge = (employeeData: DatatableEmployee) => {
    const { authAccountStatus, ...employee } = employeeData;
    setDialogAction("merge");
    setActiveEmployee(employee);
    setActiveEmployeeAuthStatus(authAccountStatus);
    setEmployeeDialogOpen(true);
  };

  const handleEdit = (employee: Employee) => {
    setDialogAction("update");
    setActiveEmployee(employee);
    setEmployeeDialogOpen(true);
  };

  if (!data || !shopId || isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-12 w-24 self-end" />
        <Skeleton className="h-24 w-full" />
        <div className="flex flex-row justify-end gap-4">
          <Skeleton className="h-12 w-24" />
          <Skeleton className="h-12 w-24" />
        </div>
      </div>
    );
  }

  const onVisibilityChange = async (id: string, visible: boolean) => {
    await employeeActions.toggleVisibility(shopId, id, visible);
    // Update cache instead of state
    updateQueryData(id, { hideInShopHero: visible });
  };

  const updateQueryData = (
    employeeId: string,
    employeeData: Partial<DatatableEmployee>
  ) => {
    patchEmployeeInAllCachedLists(
      queryClient,
      shopId,
      employeeId,
      employeeData
    );
  };

  // The one activate path. `resetPassword: false` only re-enables an existing
  // account; true generates and emails a new password (and creates the account
  // if there is none). Resolves even on failure -- errors are reported here.
  const activateAccount = async (id: string, resetPassword: boolean) => {
    const employee = data.find((e) => e.id === id);
    if (!employee) return;
    setIsSubmitting(true);
    try {
      const { emailSent, ...patch } = await activateEmployeeAccount({
        shopId,
        employee,
        resetPassword,
      });

      // Update cache instead of state
      updateQueryData(id, patch);

      if (!resetPassword) {
        toast({
          title: "Account re-activated. Their password was not changed.",
          variant: "success",
        });
      } else if (emailSent) {
        toast({
          title: "Account created and password emailed!",
          variant: "success",
        });
      } else {
        toast({
          title:
            "Account created, but we couldn't confirm the password email sent. Check with the employee, or try again.",
          variant: "default",
        });
      }
    } catch (e) {
      captureException(e);
      if (e instanceof RoleNotSavedError) {
        toast({
          title:
            "Account activated, but their role wasn't saved. Choose a role for them in the table.",
          variant: "destructive",
        });
        return;
      }
      toast({
        title: resetPassword
          ? "Unable to create/activate account. Try again in a few minutes or contact support."
          : 'Unable to re-activate this account. Try again, or choose "Reset password & email it" instead.',
        variant: "destructive",
      });
    } finally {
      // The account and role are written separately, so a failure part-way
      // can leave the cached status wrong; refetch rather than trust the patch.
      invalidateEmployeesWithAuth();
      setIsSubmitting(false);
    }
  };

  // Create & Activate: no account yet, so there is nothing to keep.
  const onActivateAccount = (id: string) => activateAccount(id, true);

  // Activate on an existing, disabled account: ask whether to reset first.
  const onReactivateAccount = (id: string) => {
    const row = data.find((e) => e.id === id);
    if (!row) return;
    const { authAccountStatus, ...employee } = row;
    setDialogAction("reactivate");
    setActiveEmployee(employee);
    setActiveEmployeeAuthStatus(authAccountStatus);
    setEmployeeDialogOpen(true);
  };

  const onResendActivationEmail = async (id: string) => {
    const employee = data.find((e) => e.id === id);
    if (!employee) return;
    setIsSubmitting(true);
    try {
      const authUser = await userActions.createAuthAccount(
        employee.email,
        employee.firstName,
        employee.lastName,
        shopId,
        employee.id
      );

      // Update cache instead of state — role is deliberately left untouched.
      updateQueryData(id, {
        authAccountStatus: authUser.data.disabled ? "inactive" : "active",
        shopHeroAuthUid: authUser.data.uid,
      });

      if (authUser.data.emailSent) {
        toast({
          title: "Password re-emailed!",
          variant: "success",
        });
      } else {
        toast({
          title:
            "Account updated, but we couldn't confirm the password email sent. Check with the employee, or try again.",
          variant: "default",
        });
      }

      setIsSubmitting(false);
    } catch (e) {
      captureException(e);
      toast({
        title:
          "Unable to resend the activation email. Try again in a few minutes or contact support.",
        variant: "destructive",
      });
      setIsSubmitting(false);
    }
  };

  const onDeactivateAccount = async (id: string) => {
    const employee = data.find((e) => e.id === id);
    if (!employee) return;
    setIsSubmitting(true);
    try {
      await employeeActions.updateEmployeeRole(shopId, id);

      const authUser = await userActions.deactivateAuthAccount(employee.email);

      // Update cache instead of state
      updateQueryData(id, {
        authAccountStatus: authUser.data.disabled ? "inactive" : "active",
      });

      setIsSubmitting(false);
    } catch (e) {
      captureException(e);
      setIsSubmitting(false);
    }
  };

  const onRoleChange = async (id: string, role: EmployeeRole) => {
    setIsSubmitting(true);
    try {
      const updateData = await employeeActions.updateEmployeeRole(
        shopId,
        id,
        role
      );
      // Update cache instead of state
      updateQueryData(id, {
        shopHeroRole: updateData?.shopHeroRole,
        shopHeroAuthUid: updateData?.shopHeroAuthUid,
      });
      setIsSubmitting(false);
    } catch (e) {
      sentry.captureException(e);
      setIsSubmitting(false);
    }
  };

  const onEmployeeEdit = async (id: string, employee: CreateEmployeeDto) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { schemaVersion, ...dataToUpdate } = employee;
    updateQueryData(id, dataToUpdate);
  };

  return (
    <>
      {activeEmployee && dialogAction === "delete" && (
        <DeleteEmployeeDialog
          shopId={shopId}
          employee={activeEmployee}
          open={employeeDialogOpen}
          setOpen={setEmployeeDialogOpen}
          authStatus={activeEmployeeAuthStatus}
          onDelete={() => setIsSubmitting(true)}
          onComplete={() => {
            invalidateEmployeesWithAuth();
            setIsSubmitting(false);
          }}
        />
      )}
      {activeEmployee && dialogAction === "restore" && (
        <RestoreEmployeeDialog
          // Keyed so a dialog still finishing for one employee can't carry its
          // in-flight state over to the next employee's dialog.
          key={activeEmployee.id}
          shopId={shopId}
          employee={activeEmployee}
          open={employeeDialogOpen}
          setOpen={setEmployeeDialogOpen}
          authStatus={activeEmployeeAuthStatus}
          conflictingEmployee={findLiveEmployeeWithEmail(
            liveEmployees,
            activeEmployee
          )}
          onRestore={() => setIsSubmitting(true)}
          onComplete={() => {
            invalidateEmployeesWithAuth();
            setIsSubmitting(false);
          }}
        />
      )}
      {activeEmployee && dialogAction === "merge" && (
        <MergeEmployeeDialog
          // Keyed so a dialog still finishing for one employee can't carry its
          // in-flight state over to the next employee's dialog.
          key={activeEmployee.id}
          shopId={shopId}
          manual={activeEmployee}
          open={employeeDialogOpen}
          setOpen={setEmployeeDialogOpen}
          onMerge={() => setIsSubmitting(true)}
          onComplete={() => {
            invalidateEmployeesWithAuth();
            setIsSubmitting(false);
          }}
        />
      )}
      {activeEmployee && dialogAction === "reactivate" && (
        <ReactivateEmployeeDialog
          // Keyed so a dialog still finishing for one employee can't carry its
          // in-flight state over to the next employee's dialog.
          key={activeEmployee.id}
          employee={activeEmployee}
          open={employeeDialogOpen}
          setOpen={setEmployeeDialogOpen}
          onActivate={(resetPassword) =>
            activateAccount(activeEmployee.id, resetPassword)
          }
        />
      )}
      {activeEmployee && dialogAction === "update" && (
        <EmployeeDialog
          shopId={shopId}
          open={employeeDialogOpen}
          setOpen={setEmployeeDialogOpen}
          initialData={activeEmployee}
          title={"Edit Team Member"}
          description={
            "Change the team member's information below to update what is shown in Shop Hero"
          }
          submitTitle={"Save"}
          cancelTitle={"Cancel"}
          onSubmit={async (data) => {
            onEmployeeEdit(activeEmployee.id, data);
            setEmployeeDialogOpen(false);
          }}
        />
      )}
      <DataTable
        // Data is based on the data list, with pagination applied
        data={tableData.slice(
          pagination.pageIndex * pagination.pageSize,
          (pagination.pageIndex + 1) * pagination.pageSize
        )}
        columns={employeeColumns({
          actions: actions,
          onVisibilityChange,
          loading: isSubmitting,
          onActivateAccount,
          onReactivateAccount,
          onDeactivateAccount,
          onResendActivationEmail,
          onRoleChange,
          terminolizeAuto: (arg: string) => terminolizeAuto(arg, ["singular"]),
        })}
        pagination={pagination}
        setPagination={setPagination}
        sorting={sorting}
        setSorting={handleSortingChange}
        rowCount={data?.length}
        loading={isLoading}
      />
    </>
  );
};
